import { ArrowDown, ArrowUp, ArrowUpDown, Check } from 'lucide-react';
import { useRef } from 'react';
import {
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
    TableHead,
    cn,
} from 'shared-ui';

import { sortOptionsFor } from './sortMenuOptions';
import type { NetworkTableSort, NetworkTableSortColumn } from './useNetworkTable';

/** One table column. Shared by the header, the skeleton and the layout column filters. */
export interface ColumnDef {
    key: NetworkTableSortColumn;
    label: string;
    /** Compact heading for the phone's narrow fixed columns; the full label is used elsewhere. */
    mobileLabel?: string;
    /** Shapes the loading skeleton (a short bar for a figure) AND selects the sort-menu wording. */
    numeric: boolean;
}

/**
 * Column heading type: Poppins 700 / text-sm / leading-5 / tracking-normal, in `--foreground`
 * (#1D1D1F light, #FAFAFA dark). Matches the toast-title idiom in `index.css`.
 */
export const COLUMN_HEADING_CLASS = 'font-poppins text-foreground text-sm leading-5 font-bold tracking-normal';

/**
 * Layout shared by every row of the sort menu: pointer cursor (the shared item defaults to the
 * menu-idiom `cursor-default`, but the design shows a hand), and a fixed-width left gutter (the
 * child span) that holds the applied option's check so the labels of checked and unchecked rows
 * sit on one vertical line. pl-2 + the 16px gutter + gap-2 lands every label 32px in — the
 * design's pl-8 — and py-1.5 / pr-2 are the design's row paddings.
 */
const MENU_ITEM_CLASS = 'cursor-pointer gap-2 py-1.5 pr-2 pl-2';

/**
 * The applied option, per the design: Origin Blue text with a leading check in the left gutter.
 * No fill of its own — the `--accent` background belongs to the hover/highlight state alone,
 * which the shared item already paints (`focus:bg-accent`; Base UI focuses the row under the
 * pointer). Base UI stamps `data-checked` on the chosen radio item, so the state styles itself.
 *
 * - `data-checked:focus:text-origin-blue` outranks the shared item's `focus:text-accent-foreground`
 *   on specificity, so highlighting the applied row does not repaint its label.
 * - `focus:**:text-origin-blue` REPLACES the shared item's `focus:**:text-accent-foreground` via
 *   tailwind-merge (same variant stack), so the leading check keeps its brand colour under
 *   highlight. On unselected rows the gutter is empty, so the rule paints nothing.
 * - The shared component's own trailing indicator (a right-aligned check) is hidden: the design
 *   puts the check in the LEFT gutter, and two checks would render otherwise.
 */
const RADIO_ITEM_CLASS =
    'data-checked:text-origin-blue data-checked:focus:text-origin-blue ' +
    'focus:**:text-origin-blue **:data-[slot=dropdown-menu-radio-item-indicator]:hidden';

/**
 * A sortable column header: an inert label beside an icon button that opens the column's sort menu.
 * `aria-sort` lives on the th, per the ARIA spec.
 *
 * Only the icon is interactive — clicking the heading text must never change the sort. The menu
 * offers exactly the two directions worded for the column's data type; there is no separate Clear
 * row — clicking the option that already holds the check clears the sort instead (`onApplySort`
 * receives null), so the checked state is a toggle.
 *
 * The menu content portals to <body> (shared-ui default), so the table's overflow containers and
 * sticky header cannot clip it, in the split and the maximised layouts alike. It is anchored to
 * the heading WRAPPER, not the icon: the design opens the menu beneath the heading, aligned with
 * the start of the label — anchoring the 28px trigger would hang it off the heading's right edge.
 */
export function SortableColumnHead({
    column,
    sort,
    onApplySort,
}: {
    column: ColumnDef;
    sort: NetworkTableSort | null;
    onApplySort: (sort: NetworkTableSort | null) => void;
}) {
    const active = sort?.column === column.key;
    const direction = active ? sort.direction : null;
    const SortIcon = direction === null ? ArrowUpDown : direction === 'asc' ? ArrowUp : ArrowDown;

    const options = sortOptionsFor(column.numeric);
    const activeOption = direction === null ? null : options.find((option) => option.direction === direction);

    // The menu's positioning anchor — the whole heading (label + icon), not just the trigger.
    const headingRef = useRef<HTMLDivElement>(null);

    return (
        <TableHead
            aria-sort={direction === null ? 'none' : direction === 'asc' ? 'ascending' : 'descending'}
            // The group scopes the hover cue to the whole header cell, not just the label — the
            // sortable surface the colour is advertising.
            className="group/colhead"
        >
            {/* gap-2: the trigger's negative margins collapse its footprint to the icon's own box
                (see below), so this IS the visual text-to-icon gap — 8px, per the design. */}
            <div ref={headingRef} className="flex items-center gap-2">
                {/* The sorted column's heading holds Origin Blue; the others take it on header
                    hover as the sortability cue. cn() resolves the clash with the base class's
                    `text-foreground` in the sorted case. */}
                <span
                    className={cn(
                        COLUMN_HEADING_CLASS,
                        active ? 'text-origin-blue' : 'group-hover/colhead:text-origin-blue',
                    )}
                >
                    {column.label}
                </span>
                <DropdownMenu>
                    <DropdownMenuTrigger
                        render={
                            /*
                             * icon-sm is a 28px hit target around the unchanged 16px icon. The
                             * negative margins collapse its footprint back to the icon's own box,
                             * so the heading occupies exactly the space the bare icon did — the
                             * extra 6px of target on each side overlaps the th padding and the
                             * inert label, where a click does nothing anyway.
                             *
                             * cursor-pointer inline: the trigger's data-slot overwrites the
                             * Button's, so the shared data-slot='button' cursor rule misses it
                             * (same clobbering as the tooltip-wrapped panel buttons). The icon
                             * declares its own brand colour for the same reason.
                             */
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                className="hover:bg-secondary data-popup-open:bg-secondary -mx-1.5 -my-1 cursor-pointer"
                                aria-label={
                                    activeOption == null
                                        ? `Sort ${column.label}`
                                        : `Sort ${column.label}, sorted ${activeOption.label}`
                                }
                            >
                                {/* Sort state is carried by the icon itself (up / down / both),
                                    never by weight or colour: every icon in the portal draws at
                                    the same 1.5px stroke, sorted or not, and aria-sort plus the
                                    trigger label state the direction. The `size` PROP must match
                                    the CSS size: absoluteStrokeWidth divides by the prop (default
                                    24), so with only a size-4 class it is a no-op and the CSS
                                    downscale thins the stroke by a third. With both, it renders
                                    at the true 1.5px matching the stats bar's Pin Stats icon. */}
                                <SortIcon
                                    className="text-origin-blue size-4"
                                    size={16}
                                    strokeWidth={1.5}
                                    absoluteStrokeWidth
                                />
                            </Button>
                        }
                    />
                    {/* Anchored to the heading, opening beneath it and aligned with its start.
                        w-auto lifts the default anchor-width sizing — the menu is as wide as its
                        rows need, floored at the design's 172px. rounded-sm replaces the shared
                        popup's rounded-lg, per the design. */}
                    <DropdownMenuContent
                        anchor={headingRef}
                        align="start"
                        sideOffset={4}
                        className="w-auto min-w-43 rounded-sm"
                    >
                        {/* The group carries only the checked state; activation is handled per
                            item, because selecting is a TOGGLE — re-choosing the checked option
                            clears the sort, and a radio group's own onValueChange never fires for
                            the already-selected value. */}
                        <DropdownMenuRadioGroup value={direction ?? undefined}>
                            {options.map((option) => (
                                // closeOnClick: radio items default to keeping the menu open (built
                                // for multi-toggle menus), but choosing a direction is a complete
                                // action — the menu closes on selection, per the design.
                                <DropdownMenuRadioItem
                                    key={option.direction}
                                    value={option.direction}
                                    closeOnClick
                                    className={`${MENU_ITEM_CLASS} ${RADIO_ITEM_CLASS}`}
                                    onClick={() => {
                                        onApplySort(
                                            option.direction === direction
                                                ? null
                                                : { column: column.key, direction: option.direction },
                                        );
                                    }}
                                >
                                    {/* The left gutter: holds the applied option's check, and is
                                        rendered (empty) on every row so all labels align. The check
                                        is decorative — aria-checked already carries the state. */}
                                    <span aria-hidden="true" className="flex w-4 shrink-0 items-center justify-center">
                                        {option.direction === direction && (
                                            <Check
                                                className="text-origin-blue size-4"
                                                size={16}
                                                strokeWidth={1.5}
                                                absoluteStrokeWidth
                                            />
                                        )}
                                    </span>
                                    {option.label}
                                </DropdownMenuRadioItem>
                            ))}
                        </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </TableHead>
    );
}
